// src/services/reviewsService.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://riskassessplus.com/api';

class ReviewsService {
    _handleUnauthorized(response) {
        if (response.status === 401) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('user');
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
            throw new Error('Session expired. Please log in again.');
        }
    }

    // Get all reviews
    async getReviews(params = {}) {
        const queryParams = new URLSearchParams({
            skip: params.skip || 0,
            limit: params.limit || 50,
            featured_only: params.featuredOnly || false,
            min_rating: params.minRating || 1
        });

        try {
            const response = await fetch(`${API_BASE_URL}/reviews/?${queryParams}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching reviews:', error);
            throw error;
        }
    }

    // Create a new review
    async createReview(reviewData) {
        try {
            const response = await fetch(`${API_BASE_URL}/reviews/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(reviewData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error creating review:', error);
            throw error;
        }
    }

    // Get review statistics
    async getReviewStats() {
        try {
            const response = await fetch(`${API_BASE_URL}/reviews/stats`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching review stats:', error);
            throw error;
        }
    }

    // Admin functions (require authentication)
    async approveReview(reviewId, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}/approve`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error approving review:', error);
            throw error;
        }
    }

    async featureReview(reviewId, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}/feature`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error featuring review:', error);
            throw error;
        }
    }

    async updateReview(reviewId, reviewData, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(reviewData)
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error updating review:', error);
            throw error;
        }
    }

    async deleteReview(reviewId, token) {
        try {
            const response = await fetch(`${API_BASE_URL}/admin/reviews/${reviewId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error deleting review:', error);
            throw error;
        }
    }

    // Get all reviews for admin (includes pending/unapproved reviews)
    async getAllReviewsForAdmin(params = {}, token) {
        const queryParams = new URLSearchParams({
            skip: params.skip || 0,
            limit: params.limit || 100
        });

        try {
            const response = await fetch(`${API_BASE_URL}/admin/reviews/?${queryParams}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            this._handleUnauthorized(response);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Error fetching admin reviews:', error);
            throw error;
        }
    }
}

export default new ReviewsService();